import React, { useMemo } from 'react';
import { Box } from '@mui/material';
import { Formik } from 'formik';
import { isEqual, pick } from 'lodash';
import { useDispatch } from 'react-redux';
import { showToast } from '@/features/toast/toastSlice';
import {
	useBatchUpdateFormFieldConfigsMutation,
	useListFormFieldConfigsQuery,
} from './api/formFieldConfigsApi';
import { FormFieldConfigScopeTabs } from './FormFieldConfigScopeTabs';
import { FormFieldConfigScopePicker } from './FormFieldConfigScopePicker';
import { FormFieldConfigsTable } from './FormFieldConfigsTable';
import { FormFieldConfigsFooter } from './FormFieldConfigsFooter';
import { useFormFieldConfigScope } from './useFormFieldConfigScope';
import { CONFIG_TYPE, EDITABLE_FIELDS, isScopeChosen } from './scopes';

/** Each changed row's changed settings, as the batch update's changes. */
const getChanges = (values, initialValues) =>
	Object.entries(values)
		.map(([id, row]) => ({
			id,
			properties: pick(
				row,
				EDITABLE_FIELDS.filter((field) => !isEqual(row[field], initialValues[id][field])),
			),
		}))
		.filter(({ properties }) => Object.keys(properties).length);

/**
 * The client config form for the page's scope: Formik values are each row's editable
 * settings by config id. The tabs, scope picker, table and footer read and change them with
 * useFormikContext. Save sends every changed row in one batch update; the list then reloads
 * and the form starts again from the saved values. A new scope starts a new form.
 */
export const FormFieldConfigsForm = () => {
	const dispatch = useDispatch();
	const { scope } = useFormFieldConfigScope();
	// currentData, not data: data keeps the previous scope's rows while a new scope loads
	const { currentData: configs } = useListFormFieldConfigsQuery(
		{ ...scope, configType: CONFIG_TYPE },
		{ skip: !isScopeChosen(scope) },
	);
	const [batchUpdateFormFieldConfigs] = useBatchUpdateFormFieldConfigsMutation();

	const initialValues = useMemo(
		() =>
			Object.fromEntries(
				(configs ?? []).map((config) => [config.id, pick(config, EDITABLE_FIELDS)]),
			),
		[configs],
	);

	const handleSubmit = async (values, { resetForm }) => {
		const result = await batchUpdateFormFieldConfigs({
			...scope,
			changes: getChanges(values, initialValues),
		});
		if (result.error) {
			// A 400 says which field and rule failed (e.g. a required field made hidden); other
			// errors aren't worded for admins
			dispatch(
				showToast({
					message:
						result.error.status === 400
							? result.error.data.error
							: 'Couldn\'t save the changes. Please try again.',
					error: true,
				}),
			);
			return;
		}
		// The saved values are the form's new starting point, so it isn't dirty while the list
		// reloads (which then brings the saved configs' ids)
		resetForm({ values });
		dispatch(
			showToast({
				message: 'Saved the changes',
				success: true,
				duration: 4000,
				autohide: true,
			}),
		);
	};

	return (
		<Formik
			key={`${scope.scope}:${scope.scopeId}`}
			initialValues={initialValues}
			enableReinitialize
			onSubmit={handleSubmit}
		>
			<>
				<FormFieldConfigScopeTabs />
				<Box sx={{ pt: 1 }}>
					<FormFieldConfigScopePicker />
					<FormFieldConfigsTable />
				</Box>
				<FormFieldConfigsFooter />
			</>
		</Formik>
	);
};

FormFieldConfigsForm.displayName = 'FormFieldConfigsForm';
